import { useT } from "@transifex/react";
import { SettingsSection, SettingsSubSection, TeacherTestDetails } from "p6m-p6u";
import { getStandardOrDefaultConfiguration } from "../../../../../helpers/PDFHelper";
import usePDFUnits from "../../../../../hooks/usePDFUnits";
import PDFCollapsible from "../../pdfCollapsible/PDFCollapsible";
import { ChangeSettingsParams } from "../../PDFConfiguration";
import PDFSettingControl from "../../pdfSettingControl/PDFSettingControl";
import PDFTextInputSettingControl from "../../pdfSettingControl/specialisedControls/pdfTextInputSettingControl/PDFTextInputSettingControl";

interface PDFTitleSettingsProps {
    changeSettings: <T extends SettingsSection, K extends SettingsSubSection<T>>(
        changeSettingsParams: ChangeSettingsParams<T, K>
    ) => void;
    teacherTestDetails: TeacherTestDetails;
    onSaveTitle: (newTitle: string) => void;
}

const PDFTitleSettings = (props: PDFTitleSettingsProps) => {
    const { pdfUnits } = usePDFUnits(props.teacherTestDetails);

    const t = useT();

    const t_header = t("Title and Topics", { _tags: "PDFConfiguration" });
    const t_showTitle = t("Title", { _tags: "PDFConfiguration" });
    const t_showVocabularyTopics = t("Vocabulary Topics", { _tags: "PDFConfiguration" });

    const settings = props.teacherTestDetails?.settings;
    const showTitle = !!settings?.titleAndTopics?.title?.show;
    const showVocabularyTopics = !!settings?.titleAndTopics?.vocabularyTopics?.show;
    const testTitle = props.teacherTestDetails.name;

    const standardOrDefaultSettings = getStandardOrDefaultConfiguration().settings;

    const handleSaveTitle = async (newTitle: string) => {
        const trimmedTitle = newTitle.trim();
        if (trimmedTitle === "") return;

        props.onSaveTitle(trimmedTitle);
    };

    return (
        <PDFCollapsible title={t_header}>
            <PDFTextInputSettingControl
                settingTitle={t_showTitle}
                isActive={showTitle ?? standardOrDefaultSettings.titleAndTopics.title.show}
                currentText={testTitle}
                preventEmptySave
                onToggle={() => {
                    props.changeSettings({
                        sectionTitle: "titleAndTopics",
                        settingTitle: "title",
                        setting: { key: "show", value: !showTitle },
                    });
                }}
                onSaveInput={handleSaveTitle}
            />
            <PDFSettingControl
                variant="toggle"
                settingTitle={t_showVocabularyTopics}
                checked={showVocabularyTopics}
                onChange={() => {
                    props.changeSettings({
                        sectionTitle: "titleAndTopics",
                        settingTitle: "vocabularyTopics",
                        setting: {
                            key: "show",
                            value: !showVocabularyTopics,
                        },
                    });
                }}
                disabled={pdfUnits.length === 0}
            />
        </PDFCollapsible>
    );
};

export default PDFTitleSettings;
